import React from "react"
import PropTypes from "prop-types"
import { Flex, Spin } from "antd"
import { useTranslation } from "react-i18next"
import { getSlotStartLabel } from "utils/experienceAvailability"
import PanelSection from "./PanelSection"
import styles from "./BookingPanel.module.css"

// Start-time chips for the chosen date and duration. The slot string ("8:00 AM - 10:00 AM") is
// what gets booked; only its start is shown. `slotStatus` (ticketed) marks a start time as sold out,
// which can't be picked, or shows its places left when few are ("· 2 left").
const StartTimeSection = ({ slots, slotStatus, selectedSlot, isLoading, hasDate, onSelect, error }) => {
  const { t } = useTranslation()

  let content
  if (!hasDate) content = <span className={styles.hint}>{t("booking_widget.experience.choose_date_first")}</span>
  else if (isLoading) content = <Spin size="small" />
  else if (slots.length === 0) content = <span className={styles.hint}>{t("booking_widget.experience.no_start_times")}</span>
  else
    content = (
      <Flex gap={8} wrap="wrap" role="radiogroup" aria-label={t("booking_widget.experience.select_start_time")}>
        {slots.map((slot) => {
          const isSelected = slot === selectedSlot
          const { isSoldOut = false, placesLeft = null } = slotStatus?.[slot] || {}
          let note = null
          if (isSoldOut) note = <span className={styles.timeChipSoldOut}> · {t("booking_widget.experience.sold_out")}</span>
          else if (placesLeft != null)
            note = <span className={styles.timeChipLeft}> · {t("booking_widget.experience.places_left", { count: placesLeft })}</span>
          return (
            <button
              key={slot}
              type="button"
              role="radio"
              aria-checked={isSelected}
              disabled={isSoldOut}
              className={`${styles.timeChip} ${isSelected ? styles.timeChipSelected : ""}`}
              onClick={() => onSelect(slot)}
            >
              {getSlotStartLabel(slot)}
              {note}
            </button>
          )
        })}
      </Flex>
    )

  return (
    <PanelSection title={t("booking_widget.experience.select_start_time")} error={error}>
      {content}
    </PanelSection>
  )
}

StartTimeSection.propTypes = {
  slots: PropTypes.arrayOf(PropTypes.string).isRequired,
  slotStatus: PropTypes.objectOf(PropTypes.shape({ isSoldOut: PropTypes.bool, placesLeft: PropTypes.number })),
  selectedSlot: PropTypes.string,
  isLoading: PropTypes.bool,
  hasDate: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  error: PropTypes.string
}

StartTimeSection.defaultProps = {
  slotStatus: null,
  selectedSlot: null,
  isLoading: false,
  hasDate: false,
  error: null
}

export default StartTimeSection
